import {BadgeCheck} from 'lucide-react'

type Variant = 'light' | 'dark' | 'compact' | 'inline'

interface FixNowBadgeProps {
  variant?: Variant
  className?: string
}

/**
 * Badge "Membre certifié FixNow" — affiche le partenariat avec FixNow.ch
 * Plateforme de dépannage à domicile en Suisse.
 */
export default function FixNowBadge({ variant = 'light', className = '' }: FixNowBadgeProps) {
  if (variant === 'compact') {
    return (
      <a
        href="https://fixnow.ch"
        target="_blank"
        rel="noopener noreferrer"
        title="Membre certifié FixNow - Plateforme de dépannage à domicile"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold uppercase tracking-wider hover:bg-emerald-100 transition-colors ${className}`}
      >
        <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
        Membre FixNow
      </a>
    )
  }

  if (variant === 'inline') {
    return (
      <a
        href="https://fixnow.ch"
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-700 text-xs font-bold uppercase tracking-wider hover:border-emerald-300 hover:bg-emerald-50 transition-colors shadow-soft ${className}`}
      >
        <BadgeCheck className="w-4 h-4" strokeWidth={2.5} />
        Membre certifié FixNow
      </a>
    )
  }

  if (variant === 'dark') {
    return (
      <a
        href="https://fixnow.ch"
        target="_blank"
        rel="noopener noreferrer"
        className={`group block bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-400/40 rounded-2xl p-5 transition-all ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
            <BadgeCheck className="w-5 h-5 text-emerald-400" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Partenaire certifié</div>
            <div className="text-white font-extrabold text-sm mt-0.5">Membre FixNow</div>
            <div className="text-navy-200 text-xs mt-1 leading-relaxed">
              Plateforme suisse de dépannage à domicile
            </div>
          </div>
        </div>
      </a>
    )
  }

  // Default: light card
  return (
    <a
      href="https://fixnow.ch"
      target="_blank"
      rel="noopener noreferrer"
      className={`group flex items-start gap-4 bg-white border border-emerald-200 hover:border-emerald-300 rounded-2xl p-5 shadow-soft hover:shadow-elevated transition-all ${className}`}
    >
      <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
        <BadgeCheck className="w-6 h-6 text-emerald-600" strokeWidth={2.2} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Partenaire certifié</div>
        <div className="text-navy-700 font-extrabold text-base mt-0.5">Membre FixNow</div>
        <div className="text-navy-500 text-sm mt-1 leading-relaxed">
          Plateforme suisse de dépannage à domicile — vérifiez en direct si NEXACO est disponible immédiatement.
        </div>
      </div>
    </a>
  )
}
